/**
 * StoryPageHeader — the story page's top bar (uispec §Header; a11y
 * contract #2, #9). Presentational only: hrefs/handlers are props.
 *
 * Anatomy:
 *   - back link + breadcrumb ("← Invoices")
 *   - display-font <h1> (the focus target — tabIndex=-1; StoryPageDetail
 *     moves focus here on load) with the friendly id
 *   - inline StatusBadge slot
 *   - Prev/Next pager: native <button disabled> at the list ends, with
 *     explicit aria-labels "Previous/Next {noun}"
 *   - action slot: Edit / Print / ⋯ (Duplicate / Send / Delete via the
 *     DropdownMenu primitive) + an Ask-Ken slot
 *
 * The back link and the menu items are rendered through caller-supplied
 * slots / props so packages/ui never imports the app router.
 */

import { ArrowLeft, ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import {
  forwardRef,
  type ReactElement,
  type ReactNode,
} from "react";

import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { cn } from "../lib/utils";

export interface StoryPageMoreItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect?: () => void;
  /** Render as a destructive (red) menu item. */
  destructive?: boolean;
  testId?: string;
  /** Show a separator ABOVE this item. */
  separatorBefore?: boolean;
}

export interface StoryPageHeaderProps {
  /** Breadcrumb text, e.g. "Invoices". */
  breadcrumbLabel: string;
  /** Back-link slot — the consumer wraps it in a router <Link>. */
  backSlot?: ReactNode;
  /** Friendly record id for the <h1>, e.g. "Invoice #INV-2049". */
  title: string;
  /** Inline status badge slot (a <StatusBadge/>). */
  statusSlot?: ReactNode;
  /** The noun used in the Prev/Next aria-labels, e.g. "invoice". */
  pagerNoun?: string;
  onPrev?: () => void;
  onNext?: () => void;
  /** Disable Prev/Next at the list ends (native disabled). */
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  /** Edit action slot (consumer wraps in router Link or button). */
  editSlot?: ReactNode;
  /** Print action slot. */
  printSlot?: ReactNode;
  /** Items for the ⋯ overflow menu (Duplicate / Send / Delete). */
  moreItems?: StoryPageMoreItem[];
  /** Ask-Ken trigger slot. */
  askKenSlot?: ReactNode;
  /** Ref to the <h1> so StoryPageDetail can move focus to it on load. */
  titleRef?: React.Ref<HTMLHeadingElement>;
  /**
   * Per-module testid namespace, e.g. "invoice-story" / "customer-story".
   * The header emits `${idPrefix}-page`, `${idPrefix}-page-title`,
   * `${idPrefix}-page-back/-status/-prev/-next/-edit/-print/-more/-ask-ken`.
   * Defaults to "invoice-story" so the invoicing call site is unchanged.
   */
  idPrefix?: string;
}

export const StoryPageHeader = forwardRef<HTMLElement, StoryPageHeaderProps>(
  (
    {
      breadcrumbLabel,
      backSlot,
      title,
      statusSlot,
      pagerNoun = "record",
      onPrev,
      onNext,
      prevDisabled,
      nextDisabled,
      editSlot,
      printSlot,
      moreItems,
      askKenSlot,
      titleRef,
      idPrefix = "invoice-story",
    },
    ref,
  ): ReactElement => {
    return (
      <header
        ref={ref}
        data-testid={`${idPrefix}-page`}
        className="border-b border-[var(--color-border-hairline)] bg-[var(--color-surface)] px-6 py-4 lg:px-8 shadow-[var(--shadow-card)]"
      >
        {/* Breadcrumb + back */}
        <div className="mb-3 flex items-center gap-2 text-sm text-[var(--color-text-soft)]">
          {backSlot ? (
            <span data-testid={`${idPrefix}-page-back`} className="inline-flex items-center gap-1">
              <ArrowLeft aria-hidden="true" size={14} className="shrink-0" />
              {backSlot}
            </span>
          ) : (
            <>
              <ArrowLeft aria-hidden="true" size={14} className="shrink-0" />
              <span>{breadcrumbLabel}</span>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* Title + status */}
          <div className="flex items-center gap-3">
            <h1
              ref={titleRef}
              tabIndex={-1}
              data-testid={`${idPrefix}-page-title`}
              className="font-display text-2xl tracking-tight text-[var(--color-text-strong)] focus-visible:outline-none"
            >
              {title}
            </h1>
            {statusSlot ? (
              <span data-testid={`${idPrefix}-page-status`}>{statusSlot}</span>
            ) : null}
          </div>

          {/* Pager + actions */}
          <div className="flex items-center gap-2">
            <div className="mr-1 flex items-center gap-1">
              <Button
                variant="solid"
                size="icon"
                data-testid={`${idPrefix}-page-prev`}
                aria-label={`Previous ${pagerNoun}`}
                onClick={onPrev}
                disabled={prevDisabled}
              >
                <ChevronLeft aria-hidden="true" size={16} />
              </Button>
              <Button
                variant="solid"
                size="icon"
                data-testid={`${idPrefix}-page-next`}
                aria-label={`Next ${pagerNoun}`}
                onClick={onNext}
                disabled={nextDisabled}
              >
                <ChevronRight aria-hidden="true" size={16} />
              </Button>
            </div>

            {editSlot ? (
              <span data-testid={`${idPrefix}-page-edit`}>{editSlot}</span>
            ) : null}
            {printSlot ? (
              <span data-testid={`${idPrefix}-page-print`}>{printSlot}</span>
            ) : null}

            {moreItems && moreItems.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="solid"
                    size="icon"
                    data-testid={`${idPrefix}-page-more`}
                    aria-label="More actions"
                  >
                    <MoreHorizontal aria-hidden="true" size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {moreItems.map((item) => (
                    <div key={item.key}>
                      {item.separatorBefore ? <DropdownMenuSeparator /> : null}
                      <DropdownMenuItem
                        data-testid={item.testId}
                        onSelect={item.onSelect}
                        className={cn(
                          "gap-2 text-sm",
                          item.destructive &&
                            "text-[var(--color-status-negative-text)]",
                        )}
                      >
                        {item.icon ? (
                          <span aria-hidden="true">{item.icon}</span>
                        ) : null}
                        {item.label}
                      </DropdownMenuItem>
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}

            {askKenSlot ? (
              <span data-testid={`${idPrefix}-page-ask-ken`}>{askKenSlot}</span>
            ) : null}
          </div>
        </div>
      </header>
    );
  },
);
StoryPageHeader.displayName = "StoryPageHeader";
